<template>
  <div>
    <nav-menu />

    <!-- Header -->
    <div class="w-full bg-gray-50 border-b border-gray-200">
      <div class="px-6 py-3 flex items-center">
        <h1 class="text-lg font-semibold text-gray-900">User Management</h1>
        <button
          @click="showCreateModal = true"
          class="ml-auto inline-flex items-center px-4 h-9 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90"
        >
          <i class="fa-solid fa-plus mr-1.5"></i>Add User
        </button>
      </div>
    </div>

    <!-- Users Table -->
    <div class="overflow-x-auto">
      <table class="min-w-full bg-white text-sm">
        <thead>
          <tr class="bg-secondary text-gray-300 text-xs font-normal uppercase tracking-wide">
            <th class="py-2 px-3 border-b border-gray-200 text-left">Name</th>
            <th class="py-2 px-3 border-b border-gray-200 text-left">Contact</th>
            <th class="py-2 px-3 border-b border-gray-200 text-center">Role</th>
            <th class="py-2 px-3 border-b border-gray-200 text-center">Team</th>
            <th class="py-2 px-3 border-b border-gray-200 text-center">Vehicle</th>
            <th class="py-2 px-3 border-b border-gray-200 text-left">Start Address</th>
            <th class="py-2 px-3 border-b border-gray-200 text-left">Region</th>
            <th class="py-2 px-3 border-b border-gray-200 text-center">SMS</th>
            <th class="py-2 px-3 border-b border-gray-200 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id" class="hover:bg-gray-50 border-b border-gray-100">
            <td class="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{{ user.name }}</td>
            <td class="px-3 py-1.5">
              <div class="flex items-center text-gray-500 text-xs"><i class="fa-solid fa-envelope w-4 text-gray-400" style="font-size:10px"></i><span class="truncate max-w-[180px]">{{ user.email }}</span></div>
              <div v-if="user.phone" class="flex items-center text-gray-500 text-xs mt-0.5"><i class="fa-solid fa-phone w-4 text-gray-400" style="font-size:10px"></i><span>{{ user.phone }}</span></div>
            </td>
            <td class="px-3 py-2 text-center">
              <select
                class="h-7 bg-gray-700 rounded text-white text-xs px-1.5 outline-none"
                v-model="user.role"
                @change="updateUserRole(user)"
              >
                <option value="admin">Admin</option>
                <option value="contractor">Remote Tester</option>
              </select>
            </td>
            <td class="px-3 py-2 text-center whitespace-nowrap">
              <span v-if="user.isTeamLeader" class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100" title="Team Leader">
                <i class="fa-solid fa-star text-yellow-500" style="font-size:11px"></i>
              </span>
              <span v-else-if="leaderNamesFor(user)" class="text-xs text-gray-600">{{ leaderNamesFor(user) }}</span>
              <span v-else class="text-gray-300">-</span>
            </td>
            <td class="px-3 py-2 text-center">
              <select
                class="h-7 bg-gray-700 rounded text-white text-xs px-1.5 outline-none"
                :value="user.assignedVehicleId || ''"
                @change="handleVehicleAssignment(user, $event.target.value)"
              >
                <option value="">None</option>
                <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.name || v.id }}</option>
              </select>
            </td>
            <td class="px-3 py-2 text-gray-500 max-w-[200px]">
              <span v-if="user.startLocation && user.startLocation.address" class="truncate block" :title="user.startLocation.address">{{ user.startLocation.address }}</span>
              <span v-else class="text-gray-300 text-xs">Default</span>
            </td>
            <td class="px-3 py-2 text-gray-500 text-xs">
              <span v-if="user.region">{{ user.region }}</span>
              <span v-else class="text-gray-300">-</span>
            </td>
            <td class="px-3 py-2 text-center">
              <div
                @click="toggleSmsAlerts(user)"
                :class="user.receiveSmsAlerts ? 'bg-green-500' : 'bg-gray-300'"
                class="relative w-9 h-5 rounded-full cursor-pointer transition-colors inline-block"
              >
                <div
                  :class="user.receiveSmsAlerts ? 'translate-x-4' : 'translate-x-0'"
                  class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                ></div>
              </div>
            </td>
            <td class="px-3 py-2 text-right whitespace-nowrap">
              <button @click="openEditModal(user)" class="w-7 h-7 rounded-full hover:bg-indigo-50 text-indigo-600 hover:text-indigo-800 inline-flex items-center justify-center" title="Edit"><i class="fa-solid fa-pen" style="font-size:12px"></i></button>
              <button @click="sendResetPasswordEmail(user)" class="w-7 h-7 rounded-full hover:bg-orange-50 text-orange-500 hover:text-orange-700 inline-flex items-center justify-center" title="Reset Password"><i class="fa-solid fa-key" style="font-size:12px"></i></button>
              <button @click="deleteProject(user)" class="w-7 h-7 rounded-full hover:bg-red-50 text-red-400 hover:text-red-600 inline-flex items-center justify-center" title="Delete"><i class="fa-solid fa-trash" style="font-size:12px"></i></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ==================== EDIT USER MODAL ==================== -->
    <div v-if="showEditModal" class="fixed z-50 inset-0 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black bg-opacity-50" @click="closeEditModal"></div>
      <div class="bg-white z-20 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between rounded-t-xl">
          <h2 class="text-base font-bold text-gray-900">Edit User</h2>
          <button @click="closeEditModal" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="px-5 py-4 space-y-3">
          <div>
            <label class="text-xs font-medium text-gray-500 mb-1 block">Name</label>
            <input v-model="editUser.name" type="text" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs font-medium text-gray-500 mb-1 block">Email</label>
              <input v-model="editUser.email" type="email" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-500 mb-1 block">Phone</label>
              <input v-model="editUser.phone" type="text" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs font-medium text-gray-500 mb-1 block">Role</label>
              <select v-model="editUser.role" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400">
                <option value="admin">Admin</option>
                <option value="contractor">Contractor</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-500 mb-1 block">Vehicle</label>
              <select v-model="editUser.assignedVehicleId" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400">
                <option :value="null">None</option>
                <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.name || v.id }}</option>
              </select>
            </div>
          </div>

          <!-- Team Leader toggle + dropdown (contractors only) -->
          <div v-if="editUser.role === 'contractor'" class="bg-gray-50 rounded-lg p-3 space-y-2.5">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-gray-600">Team Leader</span>
              <div
                @click="toggleTeamLeader(editUser)"
                :class="editUser.isTeamLeader ? 'bg-yellow-500' : 'bg-gray-300'"
                class="relative w-9 h-5 rounded-full cursor-pointer transition-colors"
              >
                <div
                  :class="editUser.isTeamLeader ? 'translate-x-4' : 'translate-x-0'"
                  class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                ></div>
              </div>
            </div>
            <div v-if="!editUser.isTeamLeader">
              <label class="text-xs text-gray-500 mb-1 block">Assign to Team Leaders</label>
              <div v-if="teamLeaders.length === 0" class="text-xs text-gray-400">No team leaders yet</div>
              <div v-else class="max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                <label v-for="tl in teamLeaders" :key="tl.id"
                  class="flex items-center px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" class="mr-2"
                    :checked="editUserLeaderIds.includes(tl.id)"
                    @change="toggleEditUserLeader(tl.id)" />
                  <span class="truncate">{{ tl.name }}</span>
                  <span v-if="tl.region" class="text-xs text-gray-400 ml-1 truncate">({{ tl.region }})</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Start Address -->
          <div>
            <label class="text-xs font-medium text-gray-500 mb-1 block">Start Address</label>
            <div v-if="editUser.startLocation && editUser.startLocation.address && !editingStartAddress" class="flex items-center h-9 bg-gray-50 rounded-lg px-3 border border-gray-200">
              <span class="text-sm text-gray-700 truncate flex-grow">{{ editUser.startLocation.address }}</span>
              <button type="button" @click="editingStartAddress = true" class="ml-2 text-gray-400 hover:text-primary text-xs"><i class="fa-solid fa-pencil"></i></button>
              <button type="button" @click="clearEditStartAddress" class="ml-1 text-gray-400 hover:text-red-500 text-xs"><i class="fa-solid fa-xmark"></i></button>
            </div>
            <div v-else class="relative">
              <input
                type="text"
                v-model="startAddressQuery"
                @input="debounceStartAddressGeocode"
                placeholder="Search address..."
                class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400"
              />
              <div v-if="startAddressResults.length" class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-full max-h-40 overflow-y-auto">
                <div v-for="(result, idx) in startAddressResults" :key="idx" @click="selectStartAddress(result)" class="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-0">
                  {{ result.place_name }}
                </div>
              </div>
              <div v-if="!editUser.startLocation && !startAddressQuery" class="text-xs text-gray-400 mt-0.5">Default: Yatala depot</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs font-medium text-gray-500 mb-1 block">Region</label>
              <select v-model="editUser.region" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400">
                <option value="">No region</option>
                <option v-for="r in availableRegions" :key="r.id" :value="r.name">{{ r.name }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-500 mb-1 block">SMS Alerts</label>
              <div class="flex items-center h-9">
                <div
                  @click="editUser.receiveSmsAlerts = !editUser.receiveSmsAlerts"
                  :class="editUser.receiveSmsAlerts ? 'bg-green-500' : 'bg-gray-300'"
                  class="relative w-9 h-5 rounded-full cursor-pointer transition-colors"
                >
                  <div
                    :class="editUser.receiveSmsAlerts ? 'translate-x-4' : 'translate-x-0'"
                    class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                  ></div>
                </div>
                <span class="ml-2 text-xs text-gray-500">Receive alerts</span>
              </div>
            </div>
          </div>
        </div>

        <div class="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3 flex justify-end space-x-2 rounded-b-xl">
          <button type="button" @click="closeEditModal" class="px-4 h-9 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="button" @click="saveUserChanges" class="px-4 h-9 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium">Save</button>
        </div>
      </div>
    </div>

    <!-- ==================== CREATE USER MODAL ==================== -->
    <div v-if="showCreateModal" class="fixed z-50 inset-0 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black bg-opacity-50" @click="showCreateModal = false"></div>
      <div class="bg-white z-20 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between rounded-t-xl">
          <h2 class="text-base font-bold text-gray-900">Create User</h2>
          <button @click="showCreateModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="px-5 py-4 space-y-3">
          <div>
            <label class="text-xs font-medium text-gray-500 mb-1 block">Name</label>
            <input v-model="name" type="text" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400" />
          </div>
          <div>
            <label class="text-xs font-medium text-gray-500 mb-1 block">Email</label>
            <input v-model="email" type="email" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs font-medium text-gray-500 mb-1 block">Password</label>
              <input v-model="password" type="password" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label class="text-xs font-medium text-gray-500 mb-1 block">Phone</label>
              <input v-model="phone" type="text" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400" />
            </div>
          </div>
          <div>
            <label class="text-xs font-medium text-gray-500 mb-1 block">Role</label>
            <select v-model="role" class="rounded-lg h-9 border border-gray-300 px-3 text-sm w-full outline-none focus:border-indigo-400">
              <option value="admin">Admin</option>
              <option value="contractor">Remote Tester</option>
            </select>
          </div>

          <!-- Team Leader toggle + dropdown (contractors only) -->
          <div v-if="role === 'contractor'" class="bg-gray-50 rounded-lg p-3 space-y-2.5">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-gray-600">Team Leader</span>
              <div
                @click="isTeamLeaderNew = !isTeamLeaderNew; if (isTeamLeaderNew) { teamLeaderIdsNew = []; }"
                :class="isTeamLeaderNew ? 'bg-yellow-500' : 'bg-gray-300'"
                class="relative w-9 h-5 rounded-full cursor-pointer transition-colors"
              >
                <div
                  :class="isTeamLeaderNew ? 'translate-x-4' : 'translate-x-0'"
                  class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                ></div>
              </div>
            </div>
            <div v-if="!isTeamLeaderNew">
              <label class="text-xs text-gray-500 mb-1 block">Assign to Team Leaders</label>
              <div v-if="teamLeaders.length === 0" class="text-xs text-gray-400">No team leaders yet</div>
              <div v-else class="max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                <label v-for="tl in teamLeaders" :key="tl.id"
                  class="flex items-center px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" class="mr-2"
                    :checked="teamLeaderIdsNew.includes(tl.id)"
                    @change="toggleNewUserLeader(tl.id)" />
                  <span class="truncate">{{ tl.name }}</span>
                  <span v-if="tl.region" class="text-xs text-gray-400 ml-1 truncate">({{ tl.region }})</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3 flex justify-end space-x-2 rounded-b-xl">
          <button type="button" @click="showCreateModal = false" class="px-4 h-9 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="button" @click="createUser(email, password)" class="px-4 h-9 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavMenu from "@/components/headers/navMenu";

import {
  getUsers,
  setFire,
  resetPassword,
  signUp,
  deleteFire,
  getVehicles,
  assignVehicleToUser,
  unassignVehicle,
  getTeamLeaders,
  getTeamMembers,
  userTeamLeaderIds
} from "../firestore/utils"
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
export default {
  data() {
    return {
      name: "",
      email: "",
      password: "",
      role: "contractor",
      phone: "",
      users: [],
      vehicles: [],
      showEditModal: false,
      editUser: {},
      showCreateModal: false,
      editingStartAddress: false,
      startAddressQuery: '',
      startAddressResults: [],
      startAddressTimer: null,
      availableRegions: [],
      teamLeaders: [],
      isTeamLeaderNew: false,
      teamLeaderIdsNew: [],
    };
  },
  computed: {
    editUserLeaderIds() {
      return userTeamLeaderIds(this.editUser);
    },
  },
  mounted() {
    this.loadUsers();
    this.loadVehicles();
    this.fetchAvailableRegions();
    this.loadTeamLeaders();
  },
  methods: {
    async deleteProject(user){
      if (window.confirm('Are you sure you want to delete?')) {
      await deleteFire({collection: "users", doc: user.id});
      this.loadUsers()
      }
    },
    async createUser(email, password) {
      try {
        const user = await signUp({
          email: email,
          password: password});
        console.log("User created:", user);
        const body = {
            email: this.email,
            role: this.role,
            phone: this.phone,
            name: this.name,
            active: true,
            avatar: "",
            password: this.password,
            isTeamLeader: this.role === 'contractor' ? (this.isTeamLeaderNew || false) : false,
            teamLeaderIds: [],
        };
        if (this.role === 'contractor' && this.isTeamLeaderNew) {
          // A leader is a member of their own team so they can assign jobs to themselves
          body.teamLeaderIds = [user.uid];
        } else if (this.role === 'contractor') {
          body.teamLeaderIds = [...this.teamLeaderIdsNew];
        }
        await setFire({
          collection: "users",
          doc: user.uid,
          body,
        });

        this.email = ''
        this.name = ''
        this.password = ''
        this.phone= ''
        this.isTeamLeaderNew = false
        this.teamLeaderIdsNew = []

        this.showCreateModal = false
        this.loadUsers()
        this.loadTeamLeaders()
      } catch (error) {
        console.error("Error creating user:", error);
      }
    },
    async loadUsers() {
      this.users = await getUsers();
    },
    async loadVehicles() {
      try {
        this.vehicles = await getVehicles();
      } catch (e) {
        console.error("Error loading vehicles:", e);
      }
    },
    async handleVehicleAssignment(user, vehicleId) {
      try {
        if (user.assignedVehicleId) {
          await unassignVehicle(user.assignedVehicleId, user.id);
        }
        if (vehicleId) {
          await assignVehicleToUser(vehicleId, user.id, user.name);
        }
        this.loadUsers();
      } catch (e) {
        console.error("Vehicle assignment error:", e);
        alert("Failed to assign vehicle. Make sure the vehicle document exists.");
      }
    },
    openEditModal(user) {
      // Normalise the legacy single teamLeaderId into the array on open, so
      // saving this user migrates the doc
      this.editUser = { ...user, teamLeaderIds: userTeamLeaderIds(user) };
      this.editingStartAddress = false;
      this.startAddressQuery = '';
      this.startAddressResults = [];
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editingStartAddress = false;
      this.startAddressQuery = '';
      this.startAddressResults = [];
    },
    debounceStartAddressGeocode() {
      if (this.startAddressTimer) clearTimeout(this.startAddressTimer);
      this.startAddressTimer = setTimeout(() => { this.geocodeStartAddress(); }, 300);
    },
    async geocodeStartAddress() {
      const q = this.startAddressQuery.trim();
      if (q.length < 3) { this.startAddressResults = []; return; }
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw&country=au&limit=5`;
        const response = await fetch(url);
        const data = await response.json();
        this.startAddressResults = data.features || [];
      } catch (e) {
        console.error('Geocode error:', e);
        this.startAddressResults = [];
      }
    },
    selectStartAddress(result) {
      const [lng, lat] = result.center;
      this.$set(this.editUser, 'startLocation', { address: result.place_name, lat, lng });
      this.startAddressQuery = '';
      this.startAddressResults = [];
      this.editingStartAddress = false;
    },
    clearEditStartAddress() {
      this.$set(this.editUser, 'startLocation', null);
      this.startAddressQuery = '';
      this.startAddressResults = [];
      this.editingStartAddress = false;
    },
    async saveUserChanges() {
      if (this.editUser.role !== 'contractor') {
        this.editUser.isTeamLeader = false;
        this.editUser.teamLeaderIds = [];
      }
      // Retire the legacy single-leader fields now the array is authoritative
      this.editUser.teamLeaderId = null;
      this.editUser.teamLeaderName = null;
      await setFire({
        collection: "users",
        doc: this.editUser.id,
        body: this.editUser,
      });

      const idx = this.users.findIndex(u => u.id === this.editUser.id);
      if (idx !== -1) this.$set(this.users, idx, { ...this.editUser });

      this.closeEditModal();
      this.loadTeamLeaders();
    },
    async sendResetPasswordEmail(user) {
      await resetPassword({ email: user.email });
      alert("Password reset link has been sent to this user");
    },
    async toggleSmsAlerts(user) {
      this.$set(user, 'receiveSmsAlerts', !user.receiveSmsAlerts);
      await setFire({
        collection: "users",
        doc: user.id,
        body: { receiveSmsAlerts: user.receiveSmsAlerts },
      });
    },
    async fetchAvailableRegions() {
      try {
        const snap = await getDocs(query(collection(db, "regions"), orderBy("name")));
        const list = [];
        snap.forEach((d) => { list.push({ ...d.data(), id: d.id }); });
        this.availableRegions = list;
      } catch (e) {
        console.error("Failed to load regions:", e);
      }
    },
    async updateUserRole(user) {
      await setFire({
          collection: "users",
          doc: user.id,
          body: { role: user.role },
        });
    },
    async loadTeamLeaders() {
      try {
        this.teamLeaders = await getTeamLeaders();
      } catch (e) {
        console.error("Error loading team leaders:", e);
      }
    },
    async toggleTeamLeader(user) {
      const newVal = !user.isTeamLeader;
      if (!newVal) {
        // Members other than the leader themselves lose this leader, but keep
        // any other leaders they report to
        const members = (await getTeamMembers(user.id)).filter(m => m.id !== user.id);
        if (members.length > 0) {
          const confirmed = confirm(`This will remove ${members.length} team member(s) from this leader's team. Continue?`);
          if (!confirmed) return;
          for (const member of members) {
            await setFire({
              collection: "users",
              doc: member.id,
              body: {
                teamLeaderIds: userTeamLeaderIds(member).filter(id => id !== user.id),
                teamLeaderId: null,
                teamLeaderName: null,
              },
            });
          }
        }
      }
      this.$set(user, 'isTeamLeader', newVal);
      // A leader is a member of their own team so they can assign jobs to themselves
      const ids = userTeamLeaderIds(user).filter(id => id !== user.id);
      this.$set(user, 'teamLeaderIds', newVal ? [user.id, ...ids] : ids);
    },
    toggleEditUserLeader(leaderId) {
      const ids = userTeamLeaderIds(this.editUser);
      this.$set(this.editUser, 'teamLeaderIds',
        ids.includes(leaderId) ? ids.filter(id => id !== leaderId) : [...ids, leaderId]);
    },
    toggleNewUserLeader(leaderId) {
      this.teamLeaderIdsNew = this.teamLeaderIdsNew.includes(leaderId)
        ? this.teamLeaderIdsNew.filter(id => id !== leaderId)
        : [...this.teamLeaderIdsNew, leaderId];
    },
    leaderNamesFor(user) {
      return userTeamLeaderIds(user)
        .map(id => (this.teamLeaders.find(tl => tl.id === id) || {}).name)
        .filter(Boolean)
        .join(', ');
    },
  },
  components: {
    NavMenu,
  },
};
</script>
